import React, { useState } from 'react';
import styled from 'styled-components';

interface FaceOption {
  value: number;
  label: string;
  mouth: string;
  brows?: boolean;
}

const FROWN = 'M11.5 22.5c2.2-2.6 6.8-2.6 9 0';
const FLAT = 'M12 21.5h8';
const SMILE = 'M11.5 19.5c2.2 2.8 6.8 2.8 9 0';
const GRIN = 'M10.5 19h11c0 3.8-2.5 5.8-5.5 5.8s-5.5-2-5.5-5.8z';

const FACES_BY_SCALE: Record<number, FaceOption[]> = {
  3: [
    { value: 1, label: 'Poor', mouth: FROWN },
    { value: 2, label: 'Okay', mouth: FLAT },
    { value: 3, label: 'Great', mouth: SMILE }
  ],
  5: [
    { value: 1, label: 'Very poor', mouth: FROWN, brows: true },
    { value: 2, label: 'Poor', mouth: FROWN },
    { value: 3, label: 'Okay', mouth: FLAT },
    { value: 4, label: 'Good', mouth: SMILE },
    { value: 5, label: 'Great', mouth: GRIN }
  ]
};

export const faceOptionsFor = (scale: number) =>
  FACES_BY_SCALE[scale] ?? FACES_BY_SCALE[5];

const SELECTED_INK = 'color-mix(in srgb, var(--color-yellow-dk) 45%, black)';

const Row = styled.div<{ $count: number }>`
  display: grid;
  grid-template-columns: repeat(${p => p.$count}, minmax(0, 1fr));
  gap: 12px;
`;

const HiddenRadio = styled.input`
  position: absolute;
  width: 1px;
  height: 1px;
  margin: -1px;
  padding: 0;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
`;

const Tile = styled.label<{
  $selected: boolean;
  $showLabel: boolean;
  $count: number;
}>`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 44px;
  padding: 2px;
  box-sizing: border-box;
  border-radius: 8px;
  border: 2px solid ${p => (p.$selected ? SELECTED_INK : 'transparent')};
  background-color: ${p =>
    p.$selected ? 'var(--color-yellow-lt)' : 'var(--color-grey-lt)'};
  color: ${p => (p.$selected ? SELECTED_INK : '#444746')};
  cursor: pointer;
  transition:
    background-color 120ms ease,
    border-color 120ms ease;

  &:hover {
    background-color: ${p =>
      p.$selected
        ? 'var(--color-yellow-lt)'
        : 'color-mix(in srgb, var(--color-grey-lt) 60%, var(--color-grey-md))'};
  }

  &:has(input:focus-visible) {
    outline: 2px solid var(--color-accent-dk);
    outline-offset: 2px;
  }

  svg {
    width: 24px;
    height: 24px;
    transform: translateY(${p => (p.$showLabel ? '-8px' : '0')});
  }

  @media (max-width: 400px) {
    height: ${p => (p.$count === 5 ? '52px' : '44px')};

    svg {
      transform: translateY(
        ${p => (p.$showLabel ? (p.$count === 5 ? '-11px' : '-8px') : '0')}
      );
    }
  }
`;

const FaceLabel = styled.span<{ $count: number }>`
  position: absolute;
  bottom: 2px;
  max-width: calc(100% - 4px);
  overflow: hidden;
  white-space: nowrap;
  font-size: clamp(10px, 2.8vw, 12px);
  line-height: 16px;
  font-weight: 600;

  @media (max-width: 400px) {
    ${p =>
      p.$count === 5 &&
      `
        white-space: normal;
        line-height: 11px;
        text-align: center;
      `}
  }
`;

const Face = ({ mouth, brows }: { mouth: string; brows?: boolean }) => (
  <svg viewBox="0 0 32 32" aria-hidden="true" focusable="false">
    <circle
      cx="16"
      cy="16"
      r="13"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    />
    <circle cx="11.8" cy="13" r="1.6" fill="currentColor" />
    <circle cx="20.2" cy="13" r="1.6" fill="currentColor" />
    {brows && (
      <path
        d="M9 9.8l4.2 1.4M23 9.8l-4.2 1.4"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
      />
    )}
    <path
      d={mouth}
      fill={mouth.endsWith('z') ? 'currentColor' : 'none'}
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

interface Props {
  scale: number;
  value: number | null;
  onChange: (value: number) => void;
}

export const FaceRating = ({ scale, value, onChange }: Props) => {
  const options = faceOptionsFor(scale);
  const [hovered, setHovered] = useState<number | null>(null);
  const [focused, setFocused] = useState<number | null>(null);
  const labelShown = (option: number) =>
    option === hovered || option === focused || option === value;

  return (
    <Row role="radiogroup" aria-label="Course rating" $count={options.length}>
      {options.map(option => (
        <Tile
          key={option.value}
          $selected={value === option.value}
          $showLabel={labelShown(option.value)}
          $count={options.length}
          onMouseEnter={() => setHovered(option.value)}
          onMouseLeave={() => setHovered(null)}
        >
          <HiddenRadio
            type="radio"
            name="course-generation-rating"
            value={option.value}
            checked={value === option.value}
            onChange={() => onChange(option.value)}
            onClick={() => onChange(option.value)}
            onFocus={() => setFocused(option.value)}
            onBlur={() => setFocused(null)}
            aria-label={option.label}
          />
          <Face mouth={option.mouth} brows={option.brows} />
          <FaceLabel
            data-testid="face-label"
            aria-hidden="true"
            $count={options.length}
          >
            {labelShown(option.value) ? option.label : ''}
          </FaceLabel>
        </Tile>
      ))}
    </Row>
  );
};
